import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Table, Form, Input, Button, DatePicker, Card, Space, message, Tag, Select, Modal,
} from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
dayjs.locale('zh-cn');
import { SearchOutlined, ReloadOutlined, CheckCircleOutlined, QrcodeOutlined } from '@ant-design/icons';
import axios from 'axios';
import { getUser } from '../../api';

const API = import.meta.env.VITE_API_BASE || '/api';
const token = () => localStorage.getItem('ar_token');
const headers = () => ({ Authorization: `Bearer ${token()}` });

const PAGE_SIZE_OPTIONS = [20, 40, 60, 80, 100];
const SIGN_STATUS_OPTIONS = [
  { label: '未签收', value: 'unsigned' },
  { label: '已签收', value: 'signed' },
];

function formatNum(n) {
  return n != null ? n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0';
}

export default function ArrivalSign() {
  const user = getUser();
  const isAdmin = user?.role === 3;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem('as_backState') || 'null');
      if (saved?.page && saved?.pageSize) {
        sessionStorage.removeItem('as_backState');
        return { current: saved.page, pageSize: saved.pageSize, total: 0 };
      }
    } catch { /* ignore */ }
    return { current: 1, pageSize: 50, total: 0 };
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // Default sign_status — read from URL once, guard against bad values
  const [defaultStatus] = useState(() => {
    const raw = searchParams.get('sign_status');
    if (raw === 'unsigned' || raw === 'signed') return raw;
    return 'unsigned';
  });

  // Saved page state from navigation back
  const [backState, setBackState] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem('as_backState') || 'null');
      if (saved?.page && saved?.pageSize) return saved;
    } catch { /* ignore */ }
    return null;
  });

  const [filters, setFilters] = useState({ sign_status: defaultStatus });
  const [qrModal, setQrModal] = useState({ open: false, orderNo: '' });

  // Form initial values
  const initialFormValues = useMemo(() => ({ sign_status: defaultStatus }), [defaultStatus]);

  // Compute selected total quantity
  const selectedTotalQty = selectedRowKeys.reduce((sum, key) => {
    const row = data.find(r => r.发货单号 === key);
    return sum + (row?.整单合计 || 0);
  }, 0);

  const fetchData = useCallback(async (page = 1, pageSize = pagination.pageSize, sort = {}) => {
    setLoading(true);
    try {
      const params = { ...filters, ...sort, page, page_size: pageSize, grouped: true };
      const r = await axios.get(`${API}/arrival/list`, { headers: headers(), params });
      setData(r.data.data);
      setPagination({ current: page, pageSize, total: r.data.total });
      setSelectedRowKeys([]);
      firstLoadDone.current = true;
    } catch { message.error('查询失败'); }
    setLoading(false);
  }, [filters, pagination.pageSize]);

  // Restore scroll position on mount when navigating back
  const scrollRestoredRef = useRef(false);
  const firstLoadDone = useRef(false);
  useEffect(() => {
    if (!firstLoadDone.current || scrollRestoredRef.current) return;
    const saved = sessionStorage.getItem('as_backState');
    if (!saved) return;
    try {
      const state = JSON.parse(saved);
      if (state.scrollTop) {
        setTimeout(() => {
          const tableBody = document.querySelector('.ant-table-body');
          if (tableBody) tableBody.scrollTop = state.scrollTop;
        }, 100);
      }
      sessionStorage.removeItem('as_backState');
      scrollRestoredRef.current = true;
    } catch { /* ignore */ }
  }, [loading, data]);

  const handleSearch = (signStatusOverride) => {
    // Use setTimeout to ensure Form state is fully updated before reading
    setTimeout(() => {
      const values = form.getFieldsValue();
      if (signStatusOverride !== undefined) values.sign_status = signStatusOverride;
      // Ensure sign_status is a string, guard against [object Object]
      const signStatus = (typeof values.sign_status === 'string' && values.sign_status !== '[object Object]') ? values.sign_status : undefined;
      const params = {};
      if (values.dateRange && values.dateRange.length === 2) {
        params.start_date = values.dateRange[0].format('YYYY-MM-DD');
        params.end_date = values.dateRange[1].format('YYYY-MM-DD');
      }
      if (values.order_no) params.order_no = values.order_no;
      if (isAdmin && values.customer) params.customer = values.customer;
      if (signStatus) params.sign_status = signStatus;
      if (values.signDate && values.signDate.length === 2) {
        params.sign_date_start = values.signDate[0].format('YYYY-MM-DD');
        params.sign_date_end = values.signDate[1].format('YYYY-MM-DD');
      }
      setFilters(params);
      // Sync sign_status to URL so it persists across navigate-back
      const nextParams = new URLSearchParams(searchParams);
      if (params.sign_status) {
        nextParams.set('sign_status', params.sign_status);
      } else {
        nextParams.delete('sign_status');
      }
      setSearchParams(nextParams, { replace: true });
    }, 0);
  };

  useEffect(() => {
    const page = backState ? backState.page : 1;
    const ps = backState ? backState.pageSize : pagination.pageSize;
    if (backState) setBackState(null);
    fetchData(page, ps);
  }, [filters]);

  const handleReset = () => {
    form.resetFields();
    setFilters({});
    setSearchParams({}, { replace: true });
  };

  const [sortInfo, setSortInfo] = useState({});

  const handleTableChange = (pag, _filters, sorter) => {
    const sf = sorter?.column ? { sort_field: sorter.field, sort_order: sorter.order } : {};
    setSortInfo(sf);
    fetchData(pag.current, pag.pageSize, sf);
  };

  // Save scroll/pagination before navigating away
  const saveBackState = () => {
    const tableBody = document.querySelector('.ant-table-body');
    const scrollTop = tableBody ? tableBody.scrollTop : 0;
    sessionStorage.setItem('as_backState', JSON.stringify({
      page: pagination.current, pageSize: pagination.pageSize, scrollTop,
    }));
  };

  const handleSignOne = (record) => {
    saveBackState();
    navigate(`/customer/arrival-sign/sign-doc?order_nos=${encodeURIComponent(record.发货单号)}`);
  };

  const handleBatchSign = () => {
    if (selectedRowKeys.length === 0) return;
    saveBackState();
    navigate(`/customer/arrival-sign/sign-doc?order_nos=${encodeURIComponent(selectedRowKeys.join(','))}`);
  };

  const columns = useMemo(() => [
    {
      title: '签收',
      width: 80,
      fixed: 'left',
      render: (_, record) => (
        record.signed ? (
          <Space size={0} direction="vertical" style={{ gap: 2 }}>
            <Tag color={record.sign_type === 'asign' ? 'purple' : 'success'} icon={<CheckCircleOutlined />}>
              {record.sign_type === 'asign' ? '已回签' : '已签'}
            </Tag>
            {record.has_signed_doc && (
              <a onClick={(e) => {
                e.preventDefault();
                saveBackState();
                const isEmbed = sessionStorage.getItem('embed_mode') === '1';
                if (isEmbed) {
                  window.open(`/s/view/${record.发货单号}`, '_blank');
                } else {
                  navigate(`/customer/arrival-sign/view/${record.发货单号}`);
                }
              }}
                 style={{ fontSize: 11, cursor: 'pointer' }}>📄 查看签收单</a>
            )}
            {record.sign_type === 'asign' && (
              <Button size="small" type="link" style={{ fontSize: 11, padding: 0, height: 18 }}
                onClick={() => {
                  axios.get(`${API}/arrival/evidence/${record.发货单号}`, { headers: headers() })
                    .then(r => {
                      if (r.data.pdf_base64) {
                        const a = document.createElement('a');
                        a.href = 'data:application/pdf;base64,' + r.data.pdf_base64;
                        a.download = `存证_${record.发货单号}.pdf`; a.click();
                      } else message.warning('暂无可下载的存证');
                    }).catch(() => message.error('下载失败'));
                }}>📥 下载存证</Button>
            )}
          </Space>
        ) : (
          <Button size="small" type="primary" ghost onClick={() => handleSignOne(record)}>
            签收
          </Button>
        )
      ),
    },
    { title: '发货日期', dataIndex: '发货日期', width: 110, render: (v) => v ? v.slice(0, 10) : '', sorter: (a, b) => (a['发货日期'] || '').localeCompare(b['发货日期'] || ''), defaultSortOrder: 'ascend' },
    { title: '发货单号', dataIndex: '发货单号', width: 200, ellipsis: true },
    { title: '快递单号', dataIndex: '顺丰单号', width: 130, ellipsis: true },
    { title: '仓库', dataIndex: '仓库', width: 100, ellipsis: true,
      render: (v) => v?.includes('上海') ? '上海仓' : '成都仓' },
    { title: '数量', dataIndex: '整单合计', width: 100, align: 'right', render: (v) => v != null ? v.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '-' },
    { title: '签收备注', dataIndex: 'remark', width: 150, ellipsis: true, render: (v) => v || '-' },
    { title: '签收日期', dataIndex: 'signed_at', width: 110, sorter: true, render: (v) => v || '-' },
    { title: '签收二维码', width: 100,
      render: (_, record) => (
        <Button size="small" icon={<QrcodeOutlined />} type="link"
          onClick={() => setQrModal({ open: true, orderNo: record.发货单号 })}>
          查看
        </Button>
      ),
    },
  ], []);

  // Warehouse helper: 是否上海仓
  const isShanghaiWarehouse = useCallback((wh) => {
    if (!wh) return false;
    return wh.includes('上海');
  }, []);

  const rowSelection = {
    selectedRowKeys,
    getCheckboxProps: (record) => ({ disabled: record.signed }),
    onSelectAll: (selected) => {
      if (selected) {
        const candidates = data.filter(r => !r.signed);
        // Check warehouse conflict
        const shanghaiKeys = candidates.filter(r => isShanghaiWarehouse(r.仓库)).map(r => r.发货单号);
        const nonShanghaiKeys = candidates.filter(r => !isShanghaiWarehouse(r.仓库)).map(r => r.发货单号);
        if (shanghaiKeys.length > 0 && nonShanghaiKeys.length > 0) {
          message.warning('上海仓与非上海仓不能同时签收，请重新选择');
          return;
        }
        setSelectedRowKeys(candidates.map(r => r.发货单号));
      } else {
        setSelectedRowKeys([]);
      }
    },
    onSelect: (record, selected) => {
      if (selected) {
        const newWarehouse = record.仓库 || '';
        const isNewShanghai = isShanghaiWarehouse(newWarehouse);
        // Check conflict: already selected items must be same type (shanghai vs non-shanghai)
        if (selectedRowKeys.length > 0) {
          const existingRow = data.find(r => r.发货单号 === selectedRowKeys[0]);
          const existingWarehouse = existingRow?.仓库 || '';
          const isExistingShanghai = isShanghaiWarehouse(existingWarehouse);
          if (isNewShanghai !== isExistingShanghai) {
            message.warning('上海仓与非上海仓不能同时签收，请重新选择');
            return;
          }
        }
        setSelectedRowKeys(prev => [...new Set([...prev, record.发货单号])]);
      } else {
        setSelectedRowKeys(prev => prev.filter(k => k !== record.发货单号));
      }
    },
  };

  return (
    <Card>
      <Form form={form} layout="inline" initialValues={initialFormValues} style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Form.Item name="dateRange" label="发货日期">
          <DatePicker.RangePicker style={{ width: 240 }} onChange={() => handleSearch()} />
        </Form.Item>
        <Form.Item name="order_no" label="发货单号">
          <Input placeholder="发货单号" style={{ width: 160 }} allowClear />
        </Form.Item>
        {isAdmin && (
          <Form.Item name="customer" label="客户名称">
            <Input placeholder="客户名称/代码" style={{ width: 180 }} allowClear />
          </Form.Item>
        )}
        <Form.Item name="sign_status" label="签收状态">
          <Select style={{ width: 110 }} options={SIGN_STATUS_OPTIONS} allowClear placeholder="全部" onChange={(val) => {
            if (val === 'unsigned') form.resetFields(['signDate']);
            form.setFieldsValue({ sign_status: val }); handleSearch(val);
          }} />
        </Form.Item>
        <Form.Item name="signDate" label="签收日期">
          <DatePicker.RangePicker style={{ width: 240 }} onChange={() => handleSearch()} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" icon={<SearchOutlined />} onClick={() => handleSearch()}>查询</Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
          </Space>
        </Form.Item>
      </Form>

      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: '#8c8c8c', fontSize: 13 }}>
          共 <strong>{pagination.total.toLocaleString()}</strong> 条
          {!isAdmin && <Tag color="blue" style={{ marginLeft: 8 }}>仅本企业数据</Tag>}
        </span>
        <Space>
          {selectedRowKeys.length > 0 && (
            <Tag color="blue">本次签收数量合计：{formatNum(selectedTotalQty)}</Tag>
          )}
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={handleBatchSign}
            disabled={selectedRowKeys.length === 0}
          >
            批量签收 ({selectedRowKeys.length})
          </Button>
        </Space>
      </div>

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={data}
        rowKey="发货单号"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
          showTotal: (t) => `共 ${t} 条`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 800, y: 'calc(100vh - 350px)' }}
        size="middle"
      />

      <Modal title={`签收二维码 - ${qrModal.orderNo}`} open={qrModal.open}
        onCancel={() => setQrModal({ open: false, orderNo: '' })}
        footer={null} width={360} centered>
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <img src={`${API}/arrival/qrcode/${qrModal.orderNo}`}
            alt="签收二维码" style={{ width: 220, height: 220 }} />
          <div style={{ marginTop: 12, color: '#666', fontSize: 13 }}>
            发货单号：{qrModal.orderNo}
          </div>
          <div style={{ marginTop: 4, color: '#999', fontSize: 12 }}>
            扫描二维码进入签收页面
          </div>
          <Button type="primary" style={{ marginTop: 12 }}
            onClick={() => {
              const a = document.createElement('a');
              a.href = `${API}/arrival/qrcode/${qrModal.orderNo}`;
              a.download = `签收二维码_${qrModal.orderNo}.png`;
              a.click();
            }}>下载二维码</Button>
        </div>
      </Modal>
    </Card>
  );
}
